import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Upload, Rocket, CheckCircle2 } from 'lucide-react';
import { dreamService } from '@/services/dreamService';

const formSchema = z.object({
  title: z.string().min(3, 'Title must be at least 3 characters'),
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  twitterHandle: z.string().optional(),
  description: z.string()
    .min(500, 'Description must be at least 500 characters')
    .max(1000, 'Description must not exceed 1000 characters'),
  videoUrl: z.string().url('Invalid URL').optional().or(z.literal('')),
  agreement: z.boolean().refine(val => val === true, {
    message: 'You must agree to the terms'
  })
});

type FormData = z.infer<typeof formSchema>;

export const SubmissionForm = () => {
  const [charCount, setCharCount] = useState(0);
  const [fileName, setFileName] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [shareLink, setShareLink] = useState('');

  const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      agreement: false
    }
  });

  // Watch description for character count
  const description = watch('description');
  const agreementValue = watch('agreement');
  
  useState(() => {
    setCharCount(description?.length || 0);
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setFileName(file.name);
      toast.success(`File "${file.name}" ready to upload`);
    }
  };

  const onSubmit = async (data: FormData) => {
    console.log('🚀 Form submitted:', data);

    const newDream = await dreamService.submitDream({
      title: data.title,
      name: data.name,
      email: data.email,
      twitterHandle: data.twitterHandle,
      description: data.description,
      videoUrl: data.videoUrl,
      mockupUrl: fileName ? `https://placeholder-for-${fileName}` : undefined,
      tags: []
    });

    const link = `${window.location.origin}/?dream=${newDream.id}`;
    setShareLink(link);
    setIsSubmitted(true);

    toast.success('🎉 Dream SaaS submitted successfully!');
  };

  if (isSubmitted) {
    return (
      <section id="submission-form" className="py-24 px-6 scroll-mt-20">
        <div className="max-w-3xl mx-auto">
          <Card className="p-12 bg-card/50 backdrop-blur-sm border-accent/50 text-center">
            <div className="mb-6 inline-flex p-6 rounded-full bg-gradient-to-br from-primary via-secondary to-accent">
              <CheckCircle2 className="w-16 h-16 text-white" />
            </div>
            
            <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
              Boom! Your idea is in the universe.
            </h2>
            
            <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
              We'll review it and reply within 48 hours. In the meantime, copy this link and share it with #DreamSaaSEmpire so the community can cheer you on!
            </p>

            <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg mb-6">
              <code className="flex-1 text-sm text-left overflow-x-auto">{shareLink}</code>
              <Button
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(shareLink);
                  toast.success('Link copied to clipboard!');
                }}
              >
                Copy
              </Button>
            </div>

            <Button
              onClick={() => {
                setIsSubmitted(false);
                reset();
                setCharCount(0);
                setFileName('');
              }}
              variant="outline"
              className="border-accent/50 hover:bg-accent/10"
            >
              Submit Another Idea
            </Button>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section id="submission-form" className="py-24 px-6 scroll-mt-20">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
            Launch Your Dream
          </h2>
          <p className="text-lg text-muted-foreground">
            Tell us about the SaaS that could change everything
          </p>
        </div>

        <Card className="p-8 md:p-12 bg-card/50 backdrop-blur-sm border-border/50">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Idea Title */}
            <div className="space-y-2">
              <Label htmlFor="title" className="text-foreground">
                Idea Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="title"
                {...register('title')}
                placeholder="e.g., AI-Powered Code Review That Actually Works"
                className="bg-background/50 border-border/50 focus:border-accent"
              />
              {errors.title && (
                <p className="text-sm text-destructive">{errors.title.message}</p>
              )}
            </div>

            {/* Your Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-foreground">
                Your Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                {...register('name')}
                placeholder="John Doe"
                className="bg-background/50 border-border/50 focus:border-accent"
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name.message}</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground">
                Email Address <span className="text-destructive">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                {...register('email')}
                placeholder="john@example.com"
                className="bg-background/50 border-border/50 focus:border-accent"
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>

            {/* Twitter Handle */}
            <div className="space-y-2">
              <Label htmlFor="twitterHandle" className="text-foreground">
                X / Twitter Handle <span className="text-muted-foreground">(optional – we'll tag you if you win @layout_dev)</span>
              </Label>
              <Input
                id="twitterHandle"
                {...register('twitterHandle')}
                placeholder="@yourhandle"
                className="bg-background/50 border-border/50 focus:border-accent"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description" className="text-foreground">
                Your Dream SaaS Description <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="description"
                {...register('description')}
                onChange={(e) => setCharCount(e.target.value.length)}
                placeholder="Describe your SaaS idea in detail. What problem does it solve? Who is it for? What makes it unique?"
                rows={8}
                className="bg-background/50 border-border/50 focus:border-accent resize-none"
              />
              <div className="flex justify-between items-center text-sm">
                <div>
                  {errors.description && (
                    <p className="text-destructive">{errors.description.message}</p>
                  )}
                </div>
                <p className={`font-medium ${
                  charCount < 500 ? 'text-muted-foreground' :
                  charCount > 1000 ? 'text-destructive' :
                  'text-accent'
                }`}>
                  {charCount} / 500-1000
                </p>
              </div>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label htmlFor="mockup" className="text-foreground">
                Upload Mockup / Screenshot <span className="text-muted-foreground">(optional – max 5 MB)</span>
              </Label>
              <div className="relative">
                <input
                  id="mockup"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label
                  htmlFor="mockup"
                  className="flex items-center justify-center gap-3 p-6 border-2 border-dashed border-border/50 rounded-lg hover:border-accent/50 transition-colors cursor-pointer bg-background/30"
                >
                  <Upload className="w-6 h-6 text-accent" />
                  <span className="text-muted-foreground">
                    {fileName || 'Click to upload or drag & drop'}
                  </span>
                </label>
              </div>
            </div>

            {/* Video URL */}
            <div className="space-y-2">
              <Label htmlFor="videoUrl" className="text-foreground">
                Video Pitch Link <span className="text-muted-foreground">(YouTube / Vimeo – optional)</span>
              </Label>
              <Input
                id="videoUrl"
                {...register('videoUrl')}
                placeholder="https://youtube.com/watch?v=..."
                className="bg-background/50 border-border/50 focus:border-accent"
              />
              {errors.videoUrl && (
                <p className="text-sm text-destructive">{errors.videoUrl.message}</p>
              )}
            </div>

            {/* Agreement Checkbox */}
            <div className="flex items-start gap-3 p-4 bg-accent/10 border border-accent/30 rounded-lg">
              <Checkbox
                id="agreement"
                checked={agreementValue}
                onCheckedChange={(checked) => setValue('agreement', checked as boolean)}
                className="mt-1 border-accent data-[state=checked]:bg-accent data-[state=checked]:border-accent"
              />
              <div className="flex-1">
                <Label htmlFor="agreement" className="text-sm cursor-pointer text-foreground/90">
                  I agree that my idea stays mine and Layout.dev can feature it (with credit)
                </Label>
                {errors.agreement && (
                  <p className="text-sm text-destructive mt-1">{errors.agreement.message}</p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              size="lg"
              className="w-full group relative px-12 py-6 text-xl font-bold bg-gradient-to-r from-primary via-secondary to-accent hover:shadow-[0_0_40px_rgba(96,165,250,0.6)] transition-all duration-300 hover:scale-[1.02]"
            >
              <span className="relative z-10 flex items-center justify-center gap-3">
                <Rocket className="w-6 h-6" />
                Launch My Dream SaaS
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-primary/80 via-secondary/80 to-accent/80 rounded-lg blur-xl opacity-50 group-hover:opacity-75 transition-opacity" />
            </Button>
          </form>
        </Card>
      </div>
    </section>
  );
};